/**
 * computeCompareScope — decides which contacts a Compare dry-run should cover,
 * excluding already-exported ones (Compare exclude-exported follow-up).
 *
 * Pure function (no React) so every branch — whole-import, a partially-exported
 * selection, and an all-exported selection — is unit-testable without driving
 * the page. Two cases:
 *
 *   · SELECTION (`selectedIds` non-empty): drop already-exported rows from the
 *     selection using the per-contact `exportStatusById` accumulator, and
 *     DISMISSED rows using `ignoredIds`; send only the pending ids.
 *     `pending`/`skipped`/`failed`/never-attempted stay in scope — they still
 *     need action, unlike an already-exported or dismissed contact. If the
 *     selection resolves to zero pending, `autoStart` is false and
 *     `nothingToCompare` is true (the modal shows the empty state, no poll).
 *
 *   · WHOLE IMPORT (`selectedIds` empty): no `contactIds` filter is sent (the
 *     dry run covers the whole import, unchanged). The divider denominator is
 *     `totalContacts - exportedCount` from the export aggregate — NOT
 *     `exportable_remaining`, which excludes W1 duplicates that Compare must
 *     keep re-surfacing on every run.
 */

/**
 * The largest EXPLICIT selection `POST /imports/{id}/compare` accepts.
 *
 * Mirrors `_MAX_EXPLICIT_SELECTION` in `packages/api/app/web/v2/imports/schemas.py`,
 * which the emitted contract carries as `maxItems: 200`. Duplicated rather than
 * derived because `maxItems` does not survive into a TypeScript type — the same
 * reason `LIST_PAGE_SIZE_BY_STAGE` (`@nu-assist/shared-fe`) restates the
 * endpoints' `le=100` as a ceiling its own test asserts.
 *
 * It exists client-side so the refusal can happen BEFORE the request, with copy
 * that says what to do about it. Relying on the server's 422 would be a worse
 * experience for a reason that is easy to miss: the recruiter's alternative
 * ("Compare all", which sends no ids) has NO limit at all, and no error message
 * arriving after the fact is going to explain that as well as the dialog can
 * before it.
 *
 * Above the cap the request would be refused twice over — by this cap at the
 * API, and at ~204 ids by the shared ALB's WAF, whose 403 carries no CORS
 * headers and reaches the browser as a bare `TypeError`. See
 * `.agent/Tasks/2026-09-12-compare-body-over-waf-limit/`.
 */
export const MAX_EXPLICIT_COMPARE_SELECTION = 200;

export interface CompareScope {
  scope: "import" | "selection";
  /** Pending-only ids for a selection; `undefined` for a whole-import dry run. */
  contactIds: string[] | undefined;
  alreadyExportedCount: number;
  /**
   * Selected rows dropped because the recruiter DISMISSED them. Exact on the
   * selection branch (it is a filter over the id list); always 0 on the
   * whole-import branch, which has no per-row knowledge — the pre-compare
   * confirmation takes that arm's number from
   * {@link compareScopeFromReviewCounts} instead.
   */
  ignoredCount: number;
  scopeTotal: number;
  autoStart: boolean;
  nothingToCompare: boolean;
}

export function computeCompareScope(args: {
  selectedIds: string[];
  exportStatusById: Map<string, string | null>;
  /**
   * Ids the server currently reports as dismissed (`review_status: "ignored"`
   * / `left_out`). The compare scope query drops these rows SERVER-side
   * (`left_out_at IS NULL` in both `list_contacts_for_compare` twins), so
   * filtering them here is not a competing rule — it is what keeps the count
   * this module hands the confirmation equal to the count the run honours.
   */
  ignoredIds: ReadonlySet<string>;
  totalContacts: number;
  exportedCount: number;
}): CompareScope {
  const { selectedIds, exportStatusById, ignoredIds, totalContacts, exportedCount } = args;

  if (selectedIds.length > 0) {
    // Exported first, dismissed second, so a row that is BOTH is attributed to
    // exactly one reason. The two counts are rendered side by side in the
    // confirmation's "Skipping …" line; double-counting would make them sum to
    // more than the rows actually dropped.
    const notExported = selectedIds.filter((id) => exportStatusById.get(id) !== "exported");
    const contactIds = notExported.filter((id) => !ignoredIds.has(id));
    return {
      scope: "selection",
      contactIds,
      alreadyExportedCount: selectedIds.length - notExported.length,
      ignoredCount: notExported.length - contactIds.length,
      scopeTotal: selectedIds.length,
      autoStart: contactIds.length > 0,
      nothingToCompare: contactIds.length === 0,
    };
  }

  return {
    scope: "import",
    contactIds: undefined,
    alreadyExportedCount: exportedCount,
    ignoredCount: 0,
    scopeTotal: totalContacts,
    autoStart: true,
    nothingToCompare: false,
  };
}

/** What a scope excludes, and what is left — the shape the confirmation both
 *  states and quotes. */
export interface CompareScopeBreakdown {
  /** Contacts the run will compare. */
  pending: number;
  /** Contacts dropped because they are already in Thrive from a prior run. */
  alreadyExported: number;
  /** Contacts dropped because the recruiter dismissed them. */
  ignored: number;
}

/**
 * The WHOLE-IMPORT arm's breakdown, taken from the unfiltered `counts.review`
 * partition rather than from the export aggregate.
 *
 * Why a second expression for a number `comparePendingCount` already produces:
 * there is no whole-import dismissal aggregate to subtract, and the accumulator
 * the selection branch uses only knows the pages that have loaded. The counts
 * block does know, exactly — its seven buckets partition the import — so this
 * is the arm's only exact answer. The two are pinned to agree on the case both
 * can answer (nothing dismissed) by test; `comparePendingCount` stays the
 * immediate value the confirmation shows until the counts fetch lands.
 *
 * Exactness rests on how the server derives the two buckets. `ignored` is
 * `left_out_at IS NOT NULL`, and `exported` is `export_status = 'exported' AND
 * NOT ignored` — `ignored` outranks it in the rule table — so the two buckets
 * are disjoint and their sum is exactly the set the run drops (the client
 * narrows away the exported rows; the server's scope query drops the dismissed
 * ones). Reorder those rules server-side and this subtraction is what breaks.
 */
export function compareScopeFromReviewCounts(
  totalContacts: number,
  review: { exported: number; ignored: number }
): CompareScopeBreakdown {
  return {
    pending: Math.max(0, totalContacts - review.exported - review.ignored),
    alreadyExported: review.exported,
    ignored: review.ignored,
  };
}

/**
 * HOW MANY contacts a run with this scope will actually compare — the one
 * number any surface may quote in front of a run.
 *
 * `scopeTotal` is NOT that number. It is the denominator the progress divider
 * reads ("5 of 12 already added"), and on the whole-import branch it is the
 * whole import while the run itself narrows to the not-yet-exported contacts
 * (`ImportDetailPage`'s narrowing branch re-runs this function over the fetched
 * full list). Quoting it in the pre-compare confirmation advertised "all 100"
 * for a run that compared 60.
 *
 * The subtraction holds on BOTH branches by construction, because
 * `alreadyExportedCount` is by definition the part of `scopeTotal` the run
 * excludes: on a selection it equals `contactIds.length` exactly, and on a
 * whole import it is `totalContacts - exportedCount` — the size of the set the
 * narrowing fetch will produce. `contactIds` is preferred when present so the
 * selection branch stays literally the enqueued list rather than a count that
 * agrees with it.
 */
export function comparePendingCount(scope: CompareScope): number {
  return scope.contactIds?.length ?? Math.max(0, scope.scopeTotal - scope.alreadyExportedCount);
}

/**
 * Whether the pre-compare confirmation should be SKIPPED and the run started
 * directly (operator, 2026-09-08: "si seleccioné todos los contactos y son
 * menos de 100 (la página) debería ir derecho a la página del compare").
 *
 * The size test is not about size. It is about what "select all" can MEAN:
 * the contacts table pages at `pageSize`, so above one page a full-looking
 * selection is ambiguous between this page and the whole set — which is the
 * ambiguity cross-page selection exists to resolve, and the ambiguity the
 * dialog exists to settle. Below one page there is nothing to settle: every
 * row is on screen and every row is ticked, so the dialog asks a question with
 * exactly one possible answer.
 *
 * THE THRESHOLD IS STAGE-DEPENDENT, and that is deliberate. Since
 * 2026-09-17 the contacts table takes its `pageSize` from `listPageSize()`
 * (`@nu-assist/shared-fe`), which resolves to 20 on local, 50 on dev and 100 on
 * prod. So the same 60-contact import SKIPS this dialog on prod and SHOWS it on
 * dev and local. The rule stays CORRECT in every environment precisely because
 * it is coupled to the page size the query actually uses — "it fits one page"
 * is the invariant, never the number 100. The operator's phrasing above quotes
 * the prod value because that is the environment they were describing; the
 * intent, not the literal, is what this function encodes.
 *
 * The consequence worth writing down: QA on dev no longer validates the prod
 * dialog behaviour, and vice versa. A "the dialog appeared when it shouldn't"
 * report from dev can be correct behaviour on dev and correct behaviour on
 * prod, differently. Reproduce such a report at the stage it came from, or
 * force the stage's page size.
 *
 * A NARROWING of the confirmation's own collapse rule, deliberately, not a
 * second rule beside it. It reuses the same equality and therefore the same
 * proof: `selectedScope.pending` is |selection ∩ pending| and
 * `allScope.pending` is |pending|, so they agree exactly when the selection
 * CONTAINS the pending set — and then the two arms enqueue identical rows.
 * Change the equality and both surfaces move together.
 *
 * The `allScope` this receives is the PAGE's blind-to-dismissals number, which
 * OVER-states (it subtracts exports but not dismissals). That direction is the
 * safe one and is why no exact-counts fetch is needed here: over-stating makes
 * the equality FAIL, so an import with dismissed rows shows the dialog it could
 * have skipped. It can never produce a false skip.
 *
 * `pending > 0` is required. A zero-row run is a legitimate state — the compare
 * modal renders "Nothing to compare" for it — but the dialog is the only place
 * that says WHY (its "Skipping N already exported and M skipped" line), and
 * dropping someone into an empty result with no explanation is worse than one
 * extra click.
 *
 * FILTERS need no special case, which is worth stating because it looks like
 * they would. A chip narrowing the table to 12 of 40 rows leaves
 * `totalContacts` at 40 (it comes from the import detail, not the page), so a
 * selection of those 12 fails the equality unless those 12 ARE the pending set
 * — in which case skipping is correct anyway.
 */
export function shouldSkipPreCompareConfirmation(args: {
  /** The IMPORT's contact count, not the filtered page's. */
  totalContacts: number;
  /** The contacts table's page size — passed in rather than imported so the
   *  rule cannot drift from the query that actually pages. Since 2026-09-17
   *  that query resolves it per stage via `listPageSize()`, so this threshold
   *  is stage-dependent; see the docstring above. */
  pageSize: number;
  /** True when ANY display filter narrows the table.
   *
   *  The nothing-selected skip rests on "the recruiter can see every row they
   *  are about to compare", and a filter falsifies that: `totalContacts` is the
   *  IMPORT's total (this function's own docstring says so), so a 300-row
   *  import filtered down to 8 visible rows would skip a dialog that is the
   *  only statement of what the run actually covers. The SELECTED branch is
   *  protected by its `pending === allScope.pending` equality; this branch had
   *  no equivalent. Reported by iter-4 review (C3). */
  hasActiveFilter?: boolean;
  /** `null` when nothing is selected. */
  selectedScope: CompareScopeBreakdown | null;
  allScope: CompareScopeBreakdown;
}): boolean {
  const { totalContacts, pageSize, selectedScope, allScope, hasActiveFilter } = args;
  // A filtered table shows a SUBSET, so "every row is visible" is false and the
  // premise of every skip below it collapses. Checked first because it applies
  // to both branches.
  if (hasActiveFilter) return false;
  // An import that does not fill one page is the whole precondition: the
  // recruiter can SEE every row they are about to compare, so a dialog
  // restating the scope tells them what is already on screen.
  if (totalContacts <= 0 || totalContacts > pageSize) return false;

  // NOTHING SELECTED, on a list that fits one page (operator, 2026-09-08).
  // This used to return false with the reasoning that "a whole-import run is
  // exactly the case where the dialog's coverage note earns its place" — true
  // in general, and wrong here: with no selection and every row visible,
  // "compare all of them" is the only reading available, so the dialog asks a
  // question with one possible answer.
  //
  // What it costs is real and worth naming: the coverage note ("N never
  // compared, M compared over a week ago") is skipped with the dialog. On a
  // list this size that note describes rows the recruiter is looking at, and
  // the Thrive ID column carries the same fact per row.
  if (selectedScope === null) return allScope.pending > 0;

  if (selectedScope.pending <= 0) return false;
  return selectedScope.pending === allScope.pending;
}
